import { Box } from '@chakra-ui/react';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

interface TooltipContextType {
  activeTooltipId: string | null;
  setActiveTooltipId: (id: string | null) => void;
}

const TooltipContext = createContext<TooltipContextType | undefined>(undefined);

interface TooltipProviderProps {
  children: React.ReactNode;
  showOverlay?: boolean;
}

export const TooltipProvider: React.FC<TooltipProviderProps> = ({
  children,
  showOverlay = false,
}) => {
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [transitionState, setTransitionState] = useState<
    'hidden' | 'entering' | 'visible' | 'exiting'
  >('hidden');
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        cancelAnimationFrame(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showOverlay) {
      setTransitionState('hidden');
      return;
    }

    if (timerRef.current !== null) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }

    if (activeTooltipId) {
      setTransitionState('entering');

      timerRef.current = window.requestAnimationFrame(() => {
        timerRef.current = window.requestAnimationFrame(() => {
          setTransitionState('visible');
          timerRef.current = null;
        });
      });
    } else {
      if (transitionState === 'visible' || transitionState === 'entering') {
        setTransitionState('exiting');

        const transitionDuration = 300;
        setTimeout(() => {
          setTransitionState('hidden');
        }, transitionDuration);
      }
    }
  }, [activeTooltipId, showOverlay]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveTooltipId(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  const isVisible = showOverlay && transitionState !== 'hidden';
  const opacity = transitionState === 'visible' ? 1 : 0;

  return (
    <TooltipContext.Provider value={{ activeTooltipId, setActiveTooltipId }}>
      {isVisible && (
        <Box
          position="fixed"
          left={0}
          right={0}
          top={0}
          bottom={0}
          zIndex={1399}
          bg="rgba(217, 217, 217, 0.45)"
          opacity={opacity}
          transition="opacity 0.3s ease-in-out"
          pointerEvents={opacity === 1 ? 'auto' : 'none'}
          data-state={transitionState}
        />
      )}
      {children}
    </TooltipContext.Provider>
  );
};

export const useTooltip = () => {
  const context = useContext(TooltipContext);
  if (context === undefined) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  return context;
};
